import { flagKeyForCode } from '~/constants/config/feature-gates';
import { useAuthStore } from '~/store/auth.store';
import { useTenantStore } from '~/store/tenant.store';

/** Result of a feature-gate lookup for a single code. */
export interface FeatureGateState {
  /** True when the code is in `FEATURE_GATES` and therefore requires a flag to render. */
  isGated: boolean;
  /** True when the route should render — either ungated, or gated and flag is enabled. */
  isAllowed: boolean;
  /** The flag key admins toggle in /platform/feature-flags, or `undefined` when ungated. */
  flagKey: string | undefined;
}

/**
 * Reactive feature-gate check for a single code. Drives sidebar visibility and any
 * runtime "is this enabled?" UI. Reads from the auth-store flag map (preferred,
 * populated by `useFeatureFlagsSync`) with a tenant-store fallback.
 *
 * Examples:
 *   const { isAllowed } = useFeatureGate('experiments');
 *   if (!isAllowed) return null;
 */
export function useFeatureGate(code: string): FeatureGateState {
  const flagKey = flagKeyForCode(code);
  const authFlags = useAuthStore((s) => s.user?.featureFlags);
  const tenantFlags = useTenantStore((s) => s.config?.featureFlags);

  if (flagKey === undefined) {
    return { isGated: false, isAllowed: true, flagKey: undefined };
  }

  // "First map with the key wins" — matches the sidebar (`nav-main.tsx`'s
  // `isCodeAllowed`) and the route guard (`resolveFlag`) so visibility and
  // navigation policies stay in lockstep.
  let isAllowed: boolean;
  if (authFlags && flagKey in authFlags) {
    isAllowed = authFlags[flagKey] === true;
  } else if (tenantFlags && flagKey in tenantFlags) {
    isAllowed = tenantFlags[flagKey] === true;
  } else {
    isAllowed = false;
  }
  return { isGated: true, isAllowed, flagKey };
}
